import React, { useEffect, useRef, useState } from 'react'
import { ExpandMore, KeyboardArrowRight } from "@mui/icons-material";
import { commonFileUrl, post_CommonFile } from '../../../services/commonFile';
import { getBranches } from '../../../services/branch';
import { useTheme } from '../../../components/hooks/useTheme';
import EmailValidatedComponent from '../../../components/EmailValidatedComponent';
import PhoneNumberInput from '../../../components/common/PhoneNumberInput';
import Cookies from 'js-cookie';
import useTenant from '../../../components/hooks/useTenant';

type Props = {
    websiteHeaderColor: string;
    setWebsiteHeaderColor: (value: string) => void;
    websiteHeaderSubColor: string;
    setWebsiteHeaderSubColor: (value: string) => void;
    websiteFooterColor: string;
    setWebsiteFooterColor: (value: string) => void;
    websiteFooterSubColor: string;
    setWebsiteFooterSubColor: (value: string) => void;
    websiteIndexPageImage: string;
    setWebsiteIndexPageImage: (value: string) => void;
    websiteSubPageImage: string;
    setWebsiteSubPageImage: (value: string) => void;
    websiteEmail: string;
    setWebsiteEmail: (value: string) => void;
    websitePhone: string;
    setWebsitePhone: (value: string) => void;
    websiteBranch: string;
    setWebsiteBranch: (value: string) => void;
    setPleaseWaitPopup: (value: boolean) => void;
    disabled: boolean;
}

const Website = ({
    websiteHeaderColor,
    setWebsiteHeaderColor,
    websiteHeaderSubColor,
    setWebsiteHeaderSubColor,
    websiteFooterColor,
    setWebsiteFooterColor,
    websiteFooterSubColor,
    setWebsiteFooterSubColor,
    websiteIndexPageImage,
    setWebsiteIndexPageImage,
    websiteSubPageImage,
    setWebsiteSubPageImage,
    websiteEmail,
    setWebsiteEmail,
    websitePhone,
    setWebsitePhone,
    websiteBranch,
    setWebsiteBranch,
    setPleaseWaitPopup,
    disabled
}: Props) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { handleTenantError }: any = useTenant();
    const [isWebsite, setIsWebsite] = useState<boolean>(true);
    const [branches, setBranches] = useState<any[]>([]);

    useEffect(() => {
        getBranches(tenantId)
            .then(data => {
                setBranches(
                    data.map((item: any) => ({
                        value: item.id,
                        label: item.branchName
                    })));

                handleTenantError(null);
            })
            .catch((error) => {
                handleTenantError(error);
            });
             // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const websiteHeaderColorRef = useRef<HTMLInputElement>(null);
    const websiteHeaderSubColorRef = useRef<HTMLInputElement>(null);
    const websiteFooterColorRef = useRef<HTMLInputElement>(null);
    const websiteFooterSubColorRef = useRef<HTMLInputElement>(null);

    const websiteIndexPageImageRef = useRef<HTMLInputElement>(null);
    const onWebsiteIndexPageImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPleaseWaitPopup(true);
        const selectedFiles = e.target.files as FileList;

        if (selectedFiles.length !== 0) {
            const formData = new FormData();
            formData.append("ImageFile", selectedFiles[0]);

            post_CommonFile(formData, tenantId)
                .then(data => {
                    setWebsiteIndexPageImage(data);
                    setPleaseWaitPopup(false);
                    handleTenantError(null);
                })
                .catch(error => handleTenantError(error));
        } else {
            setPleaseWaitPopup(false);
        }
    };

    const websiteSubPageImageRef = useRef<HTMLInputElement>(null);
    const onWebsiteSubPageImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPleaseWaitPopup(true);
        const selectedFiles = e.target.files as FileList;

        if (selectedFiles.length !== 0) {
            const formData = new FormData();
            formData.append("ImageFile", selectedFiles[0]);

            post_CommonFile(formData, tenantId)
                .then(data => {
                    setWebsiteSubPageImage(data);
                    setPleaseWaitPopup(false);
                    handleTenantError(null);
                })
                .catch(error => handleTenantError(error));
        } else {
            setPleaseWaitPopup(false);
        }
    };

    return (
        <div>
            <h5 className="mb-0 flex justify-start items-center cursor-pointer select-none"
                onClick={() => setIsWebsite(!isWebsite)}
            >
                {isWebsite ? (
                    <ExpandMore sx={{ fontSize: "20px", color: themeStyles.topBar.textColor }} />
                ) : (
                    <KeyboardArrowRight sx={{ fontSize: "20px", color: themeStyles.topBar.textColor }} />
                )}{" "}
                <div className="w-full flex flex-row justify-between items-center">
                    <div style={{ color: themeStyles.topBar.textColor }}>Website</div>
                </div>
            </h5>
            <hr />

            {isWebsite &&
                <div className="w-full px-2 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-4">
                            <div>
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website Email</label>
                                <EmailValidatedComponent
                                    text={websiteEmail}
                                    setText={setWebsiteEmail}
                                    required={true}
                                    disabled={disabled}
                                />
                            </div>

                            <div>
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website Branch</label>
                                <select
                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={websiteBranch}
                                    disabled={disabled}
                                    onChange={(e) => setWebsiteBranch(e.target.value)}
                                >
                                    {branches.map((item: any) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website Header Color</label>
                                <div className="mt-2 relative w-8 h-8">
                                    <div
                                        className="w-full h-full rounded-full border cursor-pointer"
                                        style={{ backgroundColor: websiteHeaderColor }}
                                    ></div>
                                    <input
                                        ref={websiteHeaderColorRef}
                                        type="color"
                                        value={websiteHeaderColor}
                                        disabled={disabled}
                                        onChange={(e) => setWebsiteHeaderColor(e.target.value)}
                                        className="absolute inset-0 opacity-0 cursor-pointer mt-2"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website Phone</label>
                                <PhoneNumberInput
                                    name={"phoneNumber"}
                                    value={websitePhone}
                                    setValue={setWebsitePhone}
                                    fontSize={"14px"}
                                    required={true}
                                    disabeld={disabled}
                                />
                            </div>

                            <div>
                                <label className={`text-xs font-medium invisible ${themeStyles.form.tailwindLabelTextColor}`}>Website Phone</label>
                                <input
                                    type="text"
                                    className={`w-full px-[8px] py-[8px] border rounded-md invisible ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                    value={websitePhone}
                                    disabled={disabled}
                                    onChange={(e) => setWebsitePhone(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="inline-block relative">
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                    Website Header SubColor
                                </label>

                                <div className="mt-2 relative w-8 h-8">
                                    <div
                                        className="w-full h-full rounded-full border cursor-pointer"
                                        style={{ backgroundColor: websiteHeaderSubColor }}
                                    ></div>
                                    <input
                                        ref={websiteHeaderSubColorRef}
                                        type="color"
                                        value={websiteHeaderSubColor}
                                        disabled={disabled}
                                        onChange={(e) => setWebsiteHeaderSubColor(e.target.value)}
                                        className="absolute inset-0 opacity-0 cursor-pointer mt-2"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="w-full flex flex-row gap-4 mt-2">
                        <div style={{ width: "50%" }}>
                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website Footer Color</label>
                            <div className="mt-2 relative w-8 h-8">
                                <div
                                    className="w-full h-full rounded-full border cursor-pointer"
                                    style={{ backgroundColor: websiteFooterColor }}
                                ></div>
                                <input
                                    ref={websiteFooterColorRef}
                                    type="color"
                                    value={websiteFooterColor}
                                    disabled={disabled}
                                    onChange={(e) => setWebsiteFooterColor(e.target.value)}
                                    className="absolute inset-0 opacity-0 cursor-pointer mt-2"
                                />
                            </div>
                        </div>

                        <div style={{ width: "50%" }}>
                            <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website Footer SubColor</label>
                            <div className="mt-2 relative w-8 h-8">
                                <div
                                    className="w-full h-full rounded-full border cursor-pointer"
                                    style={{ backgroundColor: websiteFooterSubColor }}
                                ></div>
                                <input
                                    ref={websiteFooterSubColorRef}
                                    type="color"
                                    value={websiteFooterSubColor}
                                    disabled={disabled}
                                    onChange={(e) => setWebsiteFooterSubColor(e.target.value)}
                                    className="absolute inset-0 opacity-0 cursor-pointer mt-2"
                                />
                            </div>
                        </div>

                    </div>

                    <div className="w-full flex flex-row gap-4 mt-2">
                        <div className="w-1/2 flex flex-col gap-4 mt-2">

                            <div className="w-full flex flex-row gap-2">
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website IndexPage Image</label>
                                {!websiteIndexPageImage ?
                                    <label
                                        style={{
                                            color: "blue",
                                            fontSize: "12px",
                                            cursor: "pointer"
                                        }}
                                        onClick={() => {
                                            if (websiteIndexPageImageRef.current) {
                                                websiteIndexPageImageRef.current.value = "";

                                            }
                                            websiteIndexPageImageRef.current?.click()
                                        }
                                        }
                                    >
                                        Add Image

                                    </label>
                                    :
                                    <label
                                        className={`text-xs font-medium text-blue-600 hover:underline cursor-pointer ml-2 ${themeStyles.form.tailwindLabelTextColor}`}
                                        onClick={(e) => {
                                            if (!disabled) {
                                                setWebsiteIndexPageImage("");
                                            }
                                        }}>
                                        Delete
                                    </label>
                                }

                            </div>

                            <div className="max-w-1/2 flex flex-row items-center gap-[5px]">
                                {websiteIndexPageImage &&
                                    <img
                                        src={`${commonFileUrl + websiteIndexPageImage}`}
                                        width={200}
                                        height={125}
                                        alt="websiteIndexPageImage"
                                    />
                                }
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                name="websiteIndexPageImage"
                                ref={websiteIndexPageImageRef}
                                disabled={disabled}
                                onChangeCapture={onWebsiteIndexPageImageChange}
                                style={{ display: "none" }}
                            />

                        </div>

                        <div className="w-1/2 flex flex-col gap-4 mt-[10px]">

                            <div style={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "row",
                                gap: "8px"
                            }}>
                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Website SubPage Image</label>
                                {!websiteSubPageImage ?
                                    <label
                                        style={{
                                            color: "blue",
                                            fontSize: "12px",
                                            cursor: "pointer"
                                        }}
                                        onClick={() => {
                                            if (websiteSubPageImageRef.current) {
                                                websiteSubPageImageRef.current.value = "";

                                            }
                                            websiteSubPageImageRef.current?.click()
                                        }}
                                    >
                                        Add Image

                                    </label>
                                    :
                                    <label
                                        className={`text-xs font-medium text-blue-600 hover:underline cursor-pointer ml-2 ${themeStyles.form.tailwindLabelTextColor}`}
                                        onClick={(e) => {
                                            if (!disabled) {
                                                setWebsiteSubPageImage("");
                                            }
                                        }}>
                                        Delete
                                    </label>
                                }

                            </div>

                            <div className="max-w-1/2 flex flex-row items-center gap-[5px]">
                                {websiteSubPageImage &&
                                    <img
                                        src={`${commonFileUrl + websiteSubPageImage}`}
                                        width={200}
                                        height={125}
                                        alt="websiteSubPageImage"
                                    />
                                }
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                name="websiteSubPageImage"
                                ref={websiteSubPageImageRef}
                                disabled={disabled}
                                onChangeCapture={onWebsiteSubPageImageChange}
                                style={{ display: "none" }}
                            />

                        </div>
                    </div>
                </div>
            }
        </div>
    )
}

export default Website